import React from 'react';
import { Download, Share2, Save, Copy } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import Button from '../../../../../components/ui/Button';
import Tooltip from '../../../../../components/ui/Tooltip';

/**
 * Компонент отображения результатов конвертации валюты
 * 
 * @param {Object} results - Результаты конвертации
 * @param {Object} currencies - Объект с информацией о валютах
 * @param {Function} saveConversion - Функция для сохранения результата
 */
const ConversionResults = ({ results, currencies, saveConversion }) => {
  if (!results) return null;
  
  // Функция копирования результата в буфер обмена
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        alert('Скопировано в буфер обмена');
      })
      .catch(err => {
        console.error('Ошибка при копировании:', err);
      });
  };
  
  // Функция для форматирования суммы с символом валюты
  const formatCurrencyWithSymbol = (amount, currencyCode) => {
    const currency = currencies[currencyCode];
    const symbol = currency?.symbol || '';
    return `${symbol} ${amount.toLocaleString(undefined, { 
      minimumFractionDigits: 2,
      maximumFractionDigits: 4
    })} ${currencyCode}`;
  };
  
  // Функция для форматирования обменного курса
  const formatExchangeRate = (rate, fromCurrency, toCurrency) => {
    return `${rate.toLocaleString(undefined, { 
      minimumFractionDigits: 4,
      maximumFractionDigits: 6
    })} ${toCurrency}`;
  };
  
  // Функция для экспорта результата в TXT
  const exportToTxt = () => {
    const { originalAmount, originalCurrency, convertedAmount, targetCurrency, exchangeRate, date } = results;
    
    const content = `Конвертация валют\n` +
      `Дата: ${date.toLocaleString()}\n\n` +
      `${originalAmount.toLocaleString()} ${originalCurrency} = ${convertedAmount.toLocaleString()} ${targetCurrency}\n` +
      `Курс: 1 ${originalCurrency} = ${exchangeRate.toLocaleString(undefined, { maximumFractionDigits: 6 })} ${targetCurrency}\n` +
      `Обратный курс: 1 ${targetCurrency} = ${(1 / exchangeRate).toLocaleString(undefined, { maximumFractionDigits: 6 })} ${originalCurrency}\n\n` +
      `Сгенерировано Gensio - Конвертер валют`;
    
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `conversion_${originalCurrency}_to_${targetCurrency}_${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  
  // Функция для шаринга результата
  const shareResult = () => {
    const { originalAmount, originalCurrency, convertedAmount, targetCurrency, exchangeRate } = results;
    
    const text = `${originalAmount.toLocaleString()} ${originalCurrency} = ${convertedAmount.toLocaleString()} ${targetCurrency} (Курс: 1 ${originalCurrency} = ${exchangeRate.toFixed(4)} ${targetCurrency})`;
    
    if (navigator.share) {
      navigator.share({
        title: 'Результат конвертации валют',
        text: text,
        url: window.location.href
      }).catch(err => {
        console.error('Ошибка при шаринге:', err);
        copyToClipboard(text);
      });
    } else {
      copyToClipboard(text);
    }
  };
  
  return (
    <ToolResult 
      title="Результат конвертации" 
      onDownload={exportToTxt}
      onShare={shareResult}
    >
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row items-center justify-between bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-lg p-4 border border-indigo-100 dark:border-indigo-800/40">
          <div className="text-center sm:text-left mb-4 sm:mb-0">
            <div className="text-sm text-indigo-600 dark:text-indigo-400 mb-1">
              {formatCurrencyWithSymbol(results.originalAmount, results.originalCurrency)}
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white flex items-center justify-center sm:justify-start">
              {formatCurrencyWithSymbol(results.convertedAmount, results.targetCurrency)}
              <Tooltip text="Копировать">
                <button 
                  onClick={() => copyToClipboard(results.convertedAmount.toString())}
                  className="ml-2 p-1 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400"
                  aria-label="Копировать результат"
                >
                  <Copy className="h-4 w-4" />
                </button>
              </Tooltip>
            </div>
          </div>
          
          <div className="flex space-x-2">
            <Button
              size="sm"
              onClick={saveConversion}
              icon={<Save className="h-4 w-4" />}
            >
              Сохранить
            </Button>
          </div>
        </div>
        
        <div className="space-y-3">
          <ResultItem 
            label="Исходная сумма" 
            value={formatCurrencyWithSymbol(results.originalAmount, results.originalCurrency)}
          />
          
          <ResultItem 
            label="Конвертированная сумма" 
            value={formatCurrencyWithSymbol(results.convertedAmount, results.targetCurrency)}
            highlight
            color="indigo"
          />
          
          <ResultItem 
            label="Обменный курс" 
            value={`1 ${results.originalCurrency} = ${formatExchangeRate(results.exchangeRate, results.originalCurrency, results.targetCurrency)}`}
          />
          
          <ResultItem 
            label="Обратный курс" 
            value={`1 ${results.targetCurrency} = ${formatExchangeRate(results.reverseRate, results.targetCurrency, results.originalCurrency)}`}
          />
          
          <ResultItem 
            label="Дата обновления" 
            value={results.date.toLocaleDateString('ru-RU', { 
              day: 'numeric', 
              month: 'long', 
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          />
        </div>
        
        <div className="mt-4 text-sm text-gray-600 dark:text-gray-400">
          <p>Курсы обновляются регулярно для обеспечения максимальной точности. 
          Обратите внимание, что фактические курсы в банках и платежных системах могут отличаться 
          из-за комиссий и наценок.</p>
        </div>
      </div>
    </ToolResult>
  );
};

export default ConversionResults;